import * as angular from 'angular';
import { Decimal } from 'decimal.js';

(function () {
    'use strict';

    /**
     * @ngdoc directive
     * @name pkpColumnFilter
     * @memberof app
     *
     * @restrict E
     * @requires app.pkpFilterTable
     * @scope {}
     *
     * @description
     * This directive is a column filter for a certain column. It lists checkboxes with suggested values to filter for.
     * The suggestions can be filtered again with a text field.
     *
     * A column filter can be one of four categories: `valueType`, `pickerValue`, `pickerValueGasDay` and suggestion-based.
     *
     * * `valueType`: A value that is entered and filtered by.
     * * `pickerValue`: A date time picker whose value is filtered by.
     * * `pickerValueGasDay`: A date time picker using gas days whose value is filtered by.
     * * suggestion-based: An input field that is sent to the backend to provide suggestions to filter by. This can be
     *   again of two types. One with a predefined set of suggestions that won't be requested from the server. Like for
     *   direction they would be preset to exit, entry and exit/entry. In the other case suggestions would be provided
     *   according to what the user has typed into the column filter. The user can then select from the suggestions or
     *   not. If not, all the suggestions will be selected.
     *
     * Depending on that different functions will be called to update the scope and handle the filtering.
     *
     * Suggestion-based column filters are again split into two characteristics. There are preset suggestions and there
     * are suggestions that are dynamically fetched from the backend as you type. The latter is more complex:
     *
     * * If you have entered a search query but not selected any suggestion, that actually means you've selected all of
     *   them.
     * * If you remove the search query, all of your selected suggestions will also be removed and this is how you reset
     *   the filter.
     *
     * @param {string} filterColumn The column to filter by from {@link app.COLUMN_FILTER#COL}. Is used in combination
     *  with data from the {@link app.pkpFilterTable} to access the model to save the column filter selection to.
     *  Will prevent suggested values.
     * @param {string} suggestionsName If the `filterColumn` is not unique, this is used to distinguish between the
     * suggestions that should be taken from column filter constants/service and the actual name of the query parameter.
     * @param {expression} valueType Appends to `filterColumn` like `pickerValue`. Is used to initialize input fields
     *  for numbers. Will prevent suggested values.
     * @param {expression} pickerValue Will be appended to `filterColumn`. Will initialize a date time picker over
     *  an ordinary input field if set.
     * @param {expression} pickerValueGasDay Will be appended to `filterColumn`. Will initialize a date time picker which
     *  is using gas days over an ordinary input field if set.
     * @param {*} initialValue This is the initial value the column filter option will be assigned to.
     * @param {"KILO"|"MEGA"|null} [physicalUnit=null] For `valueType`s the value will be multiplied by 1000 on
     *  submission if the physical unit is MWh/h (`MEGA`) and the other way round. When not passed--i.e. for price
     *  fields--no further action will be taken. Additionally the filter's value will be dynamically converted when the
     *  physical unit is changed.
     * @param {"UNIT"|"SUBUNIT"|null} [costSetting=null] For `valueType`s the value will be dynamically converted when
     *  the cost setting is changed. No further actions will be taken, when this is not set.
     *
     * @author flzemke.btc
     */
    angular.module('app').directive('pkpColumnFilter', pkpColumnFilter);

    function pkpColumnFilter(
        $timeout,
        ColumnFilter,
        columnFilterService,
        DATES,
        COLUMN_FILTER
    ) {
        return {
            restrict: 'E',
            scope: {
                filterResource: '<?',
                filterColumn: '=',
                suggestionsName: '=?',
                valueType: '@',
                pickerValue: '@',
                pickerValueGasDay: '@',
                initialValue: '=?',
                physicalUnit: '=?',
                costSetting: '=?'
            },
            require: '^^pkpFilterTable',
            template: require('app/_directives/pkp-column-filter.directive.html'),
            link: linkFn
        };

        function linkFn(scope, elem, attr, pkpFilterTableCtrl) {
            scope.DATES = DATES;
            scope.uiLoading = false;
            scope.uiErrorLoading = false;
            scope.physicalUnit =
                scope.physicalUnit === 'MEGA' || scope.physicalUnit === 'KILO'
                    ? scope.physicalUnit
                    : null;
            scope.costSetting =
                scope.costSetting === 'UNIT' || scope.costSetting === 'SUBUNIT'
                    ? scope.costSetting
                    : null;
            scope.presetSuggestions = columnFilterService.getPresetSuggestions(
                scope.suggestionsName || scope.filterColumn
            );
            scope.filterSuggestionsText = {
                value: ''
            };
            scope.dateTimePicker = {
                value: undefined
            };
            scope.filter = {
                value: ''
            };
            scope.selectAll = {
                value: false
            };
            scope.suggestions = [];
            scope.onSuggestionsToggle = onSuggestionsToggle;
            scope.onFilterSuggestionsTextChange = onFilterSuggestionsTextChange;
            scope.submitFilter = submitFilter;
            scope.updateFilterByValueType = updateFilterByValueType;
            scope.syncSelectAllButton = syncSelectAllButton;
            scope.selectAllSuggestions = selectAllSuggestions;

            scope.$watch(
                function () {
                    return scope.dateTimePicker.value;
                },
                function (newVal, oldVal) {
                    if (newVal === oldVal) {
                        return;
                    }
                    updatePicker();
                    updatePickerGasDay();
                }
            );

            scope.$watch(
                function () {
                    if (
                        scope.physicalUnit &&
                        pkpFilterTableCtrl.currentFilter
                    ) {
                        return scope.physicalUnit;
                    }
                },
                function (newValue, oldValue) {
                    if (
                        !newValue ||
                        angular.equals(newValue, oldValue) ||
                        !pkpFilterTableCtrl.currentFilter[
                            scope.filterColumn + '-' + scope.valueType
                        ]
                    ) {
                        return;
                    }

                    if (newValue === 'MEGA') {
                        scope.filter.value /= 1000;
                    } else if (newValue === 'KILO') {
                        scope.filter.value *= 1000;
                    }
                }
            );

            scope.$watch(
                function () {
                    return scope.costSetting;
                },
                function (newCostSetting) {
                    if (!newCostSetting || !scope.filter.value) {
                        return;
                    }

                    if (newCostSetting === 'UNIT') {
                        scope.filter.value = new Decimal(scope.filter.value)
                            .dividedBy(100)
                            .toDecimalPlaces(8)
                            .toNumber();
                    } else if (newCostSetting === 'SUBUNIT') {
                        scope.filter.value = new Decimal(scope.filter.value)
                            .times(100)
                            .toDecimalPlaces(8)
                            .toNumber();
                    }
                }
            );

            (function init() {
                if (!scope.initialValue) {
                    return;
                }

                if (scope.pickerValue) {
                    scope.dateTimePicker.value = scope.initialValue || {};
                    updatePicker();
                } else if (scope.pickerValueGasDay) {
                    scope.dateTimePicker.value = scope.initialValue || {};
                    updatePickerGasDay();
                } else if (scope.valueType) {
                    scope.filter.value = scope.initialValue;
                } else {
                    if (angular.isArray(scope.initialValue)) {
                        scope.filterSuggestionsText.value =
                            scope.initialValue.join(', ');
                    }
                }
            })();

            /**
             * Triggers when the dropdown for suggestions opens or closes.
             * This is only important for suggestion-based column filters.
             *
             * @memberof pkpColumnFilter
             * @param {boolean} wasOpened Whether or not the dropdown event was triggered by opening or closing.
             * @returns {void} This is just doing assignments to the column filter model.
             */
            function onSuggestionsToggle(wasOpened) {
                if (scope.presetSuggestions) {
                    $timeout(function () {
                        scope.suggestions =
                            columnFilterService.addSelectedFlags(
                                pkpFilterTableCtrl.currentFilter[
                                    scope.filterColumn
                                ],
                                scope.presetSuggestions
                            );
                        scope.truncated = false;
                    });
                } else if (
                    !wasOpened &&
                    !!scope.filterSuggestionsText.value &&
                    !columnFilterService.filterSelectedSuggestions(
                        scope.suggestions
                    ).length
                ) {
                    selectAllSuggestions(true);
                    submitFilter();
                }
            }

            /**
             * Requests the suggestions dynamically when the search query is at least three chars long.
             * This is for suggestion-based column filters with dynamic suggestions.
             *
             * @memberof pkpColumnFilter
             * @returns {void}
             */
            function onFilterSuggestionsTextChange() {
                if (!scope.filterSuggestionsText.value) {
                    selectAllSuggestions(false);
                    submitFilter();
                } else {
                    requestSuggestions();
                }
            }

            /**
             * Sets the label optionally and the column filter model when a suggestion is checked/unchecked or the user
             * has typed a new search query for suggestions.
             * This is used when the column filter is neither {@link pkpColumnFilter#valueType} nor
             * {@link pkpColumnFilter#pickerValue}, but works with an input field based on suggestions.
             *
             * @memberof pkpColumnFilter
             * @param {boolean} [updateLabelAsWell=true] In case the input field label should also be updated. In some
             *  cases you want to prevent that when the user has just entered a search query and automatically all
             *  options are pre-selected. You would not want the search query to be overridden by the pre-selection.
             * @param {boolean} [calledWithTimeout=false] This function should be called with `$timeout` service,
             *  so that changes on the scope will be applied. If this isn't set, the function will call itself with the
             *  `$timeout` service itself, so you can basically ignore this parameter.
             * @returns {void} This is just doing assignments to the column filter model.
             */
            function submitFilter(updateLabelAsWell?, calledWithTimeout?) {
                calledWithTimeout = !!calledWithTimeout;
                updateLabelAsWell = angular.isUndefined(updateLabelAsWell)
                    ? true
                    : !!updateLabelAsWell;

                if (!calledWithTimeout) {
                    $timeout(function () {
                        submitFilter(updateLabelAsWell, true);
                    });
                }

                var selectedFilters =
                    columnFilterService.filterSelectedSuggestions(
                        scope.suggestions
                    );
                var selectedFilterValues = [];
                var selectedFilterLabels = [];

                for (var i = 0; i < selectedFilters.length; i++) {
                    selectedFilterValues.push(selectedFilters[i].value);
                    selectedFilterLabels.push(selectedFilters[i].label);
                }

                if (updateLabelAsWell) {
                    scope.filterSuggestionsText.value =
                        selectedFilterLabels.join(', ');
                }
                pkpFilterTableCtrl.currentFilter[scope.filterColumn] =
                    selectedFilterValues;
            }

            /**
             * A change in `valueTyp`-based input field should trigger this method which assigns the value to the
             * column filter model.
             *
             * @memberof pkpColumnFilter
             * @see {@link pkpColumnFilter#valueType}
             * @see {@link pkpColumnFilter#physicalUnit}
             * @returns {void} This is just doing assignments to the column filter model.
             */
            function updateFilterByValueType() {
                if (!scope.filter.value) {
                    delete pkpFilterTableCtrl.currentFilter[
                        scope.filterColumn + '-' + scope.valueType
                    ];
                    return;
                }

                if (!scope.physicalUnit) {
                    pkpFilterTableCtrl.currentFilter[
                        scope.filterColumn + '-' + scope.valueType
                    ] = scope.filter.value;
                } else {
                    pkpFilterTableCtrl.currentFilter[
                        scope.filterColumn + '-' + scope.valueType
                    ] =
                        scope.physicalUnit === 'MEGA'
                            ? scope.filter.value * 1000
                            : Math.floor(scope.filter.value);
                }
            }

            /**
             * A change in the date time picker should trigger this method which assigns the value to the column filter
             * model.
             *
             * @memberof pkpColumnFilter
             * @see {@link pkpColumnFilter#pickerValue}
             * @returns {void} This is just doing assignments to the column filter model.
             */
            function updatePicker() {
                if (angular.isUndefined(scope.pickerValue)) {
                    return;
                }

                if (!scope.dateTimePicker.value) {
                    pkpFilterTableCtrl.currentFilter[
                        scope.filterColumn + scope.pickerValue
                    ] = null;
                } else {
                    pkpFilterTableCtrl.currentFilter[
                        scope.filterColumn + scope.pickerValue
                    ] = scope.dateTimePicker.value;
                }
            }

            /**
             * A change in the date time picker (using gas days) should trigger this method which assigns
             * the value to the column filter model.
             *
             * @memberof pkpColumnFilter
             * @see {@link pkpColumnFilter#pickerValueGasDay}
             * @returns {void} This is just doing assignments to the column filter model.
             */
            function updatePickerGasDay() {
                if (angular.isUndefined(scope.pickerValueGasDay)) {
                    return;
                }

                if (!scope.dateTimePicker.value) {
                    pkpFilterTableCtrl.currentFilter[
                        scope.filterColumn + scope.pickerValueGasDay
                    ] = null;
                } else {
                    pkpFilterTableCtrl.currentFilter[
                        scope.filterColumn + scope.pickerValueGasDay
                    ] = scope.dateTimePicker.value;
                }
            }

            /**
             * Based on the text in the column filter, suggestions to filter by are requested from the server.
             *
             * @memberof pkpColumnFilter
             * @returns {void}
             */
            function requestSuggestions() {
                scope.uiLoading = true;

                var param = {
                    filterColumn: scope.filterColumn,
                    filterTable: pkpFilterTableCtrl.filterTable,
                    infix: scope.filterSuggestionsText.value
                };
                angular.extend(param, pkpFilterTableCtrl.currentFilter);

                if (!scope.filterResource) {
                    ColumnFilter.query(
                        param,
                        function (res) {
                            scope.suggestions = res.options;
                            scope.truncated = res.truncated;
                            selectAllSuggestions(true);
                            submitFilter(false);
                        },
                        function () {
                            scope.uiErrorLoading = true;
                        }
                    ).$promise.finally(function () {
                        scope.uiLoading = false;
                    });
                }
            }

            /**
             * The "Select all" checkbox synchronizes according to the selected suggestions.
             * When all suggestions have been selected, the "select all" checkbox will also be selected and vise versa.
             *
             * @memberof pkpColumnFilter
             * @returns {void}
             */
            function syncSelectAllButton() {
                var selectedSuggestions =
                    columnFilterService.filterSelectedSuggestions(
                        scope.suggestions
                    );
                scope.selectAll.value =
                    selectedSuggestions.length === scope.suggestions.length;
            }

            /**
             * The "Select all" button selects all of the listed suggestions.
             *
             * @memberof pkpColumnFilter
             * @param {boolean} [selectAll] Defaults to the model-bound select all button, if not set right this model
             *  value will be assigned to `selectAll`.
             * @returns {boolean} `true` if all were selected, `false` otherwise.
             */
            function selectAllSuggestions(selectAll) {
                selectAll = angular.isUndefined(selectAll)
                    ? scope.selectAll.value
                    : !!selectAll;
                scope.selectAll.value = selectAll;

                angular.forEach(scope.suggestions, function (obj) {
                    obj._selected = selectAll;
                });

                return selectAll;
            }
        }
    }
})();
